/**
 * Each node in the canvas that has a DomNode attached to it will also have a data-w-id attribute that stores the node's guid.
 * In most cases, these IDs are not persisted to the server, but they saved for typographic node descendants (micro nodes).
 */
export const WEBFLOW_NODE_ID_ATTR = 'data-w-id';

/**
 * The dom node attribute used to express the exact path to a dom node.
 */
export const WEBFLOW_NODE_ID_PATH_ATTR = 'data-wf-id';

/**
 * Attribute on the document element that identifies the siteId.
 */
export const WEBFLOW_SITE_ID_ATTR = 'data-wf-site';

/**
 * Used frequently for DomNodeAnchor, etc.
 */
export const WEBFLOW_SITE_IFRAME_ID = 'site-iframe-next';
export const WEBFLOW_SITE_IFRAME_SELECTOR = '#' + WEBFLOW_SITE_IFRAME_ID;

/**
 * For symbol instance descendants we keep the original id in a different attribute.
 */
export const webflowNodeInstanceIdAttr = 'data-w-instance-of';

/**
 * Attribute on component root elements.
 * Used in Content Editor to quickly find the component root from any nested element.
 */
export const WEBFLOW_COMPONENT_ROOT_ATTR = 'data-w-component-root';

/**
 * Attribute on elements whose children has a children binding that is not a CMS binding.
 */
export const BINDING_ON_CHILDREN_ATTR = 'data-has-binding-on-children';

/**
 * Attribute set on the rich text container element when its children
 * are bound to a CMS field. Used to identify CMS-bound rich text
 * during DOM traversal on click/hover.
 */
export const CMS_BINDING_ON_CHILDREN_ATTR = 'data-has-cms-binding-on-children';

/**
 * Attribute used on Canvas native HTML nodes.
 * This attribute contains the CMS collection id to which the content
 * of the native node is bound.
 */
export const CMS_COLLECTION_ID_ATTR = 'data-w-collection-id';
/**
 * Attribute used on Canvas native HTML nodes.
 * This attribute contains whether the collection is CMS or Ecommerce.
 */
export const CMS_COLLECTION_SOURCE_ATTR = 'data-w-collection-source';
/**
 * Attribute used on Canvas native HTML nodes.
 * This attribute contains the CMS item id to which the content
 * of the native node is bound.
 */
export const CMS_ITEM_ID_ATTR = 'data-w-item-id';

export const VALID_RICH_TEXT_NODES: Set<string> = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'figure',
  'figcaption',
  'a',
  'strong',
  'em',
  'sup',
  'sub',
  'div',
  'span',
  'img',
  'iframe',
  'ul',
  'ol',
  'li',
  'br',
  'code',
]);
export const VALID_HTML_EMBED_NODES: Set<string> = new Set([
  ...VALID_RICH_TEXT_NODES,
  'script',
  'style',
  'link',
  // Table elements
  'table',
  'caption',
  'thead',
  'tr',
  'th',
  'td',
  'tbody',
  'tfoot',
  // Layout elements
  'hr',
  'aside',
  'pre',
  'i',
  'header',
  // Form elements
  'form',
  'input',
  'textarea',
  'select',
  'option',
  'optgroup',
  'datalist',
  'button',
  'label',
  'fieldset',
  'legend',
  // Disclosure elements
  'details',
  'summary',
]);
/**
 * Includes also HTML tags not valid inside rich texts since from the
 * public API this is used for all page contents.
 */
export const VALID_PAGE_CONTENT_NODES = new Set([
  ...VALID_RICH_TEXT_NODES,
  'label',
]);
export const VALID_TYPOGRAPHIC_CONTENT_NODES: Array<string> = [
  'a',
  'strong',
  'em',
  'br',
  'span',
];
/** RTE link attributes, used when an element lacks a domNode */
export const RTE_LINK_TYPE_ATTR = 'data-rt-link-type';
export const RTE_LINK_PAGE_ID_ATTR = 'data-rt-link-pageid';
export const RTE_LINK_ITEM_ID_ATTR = 'data-rt-link-itemid';
export const RTE_LINK_COLLECTION_ID_ATTR = 'data-rt-link-collectionid';
export const RTE_LINK_DOM_ID_ATTR = 'data-rt-link-domid';
export const RTE_LINK_DATA_ATTRS: Array<string> = [
  RTE_LINK_PAGE_ID_ATTR,
  RTE_LINK_TYPE_ATTR,
  RTE_LINK_ITEM_ID_ATTR,
  RTE_LINK_COLLECTION_ID_ATTR,
  RTE_LINK_DOM_ID_ATTR,
];
/** RTE figure attributes, used when a figure lacks a domNode */
export const RTE_FIGURE_TYPE_ATTR = 'data-rt-type';
export const RTE_FIGURE_ALIGN_ATTR = 'data-rt-align';
export const RTE_FIGURE_MAX_WIDTH_ATTR = 'data-rt-max-width';
export const RTE_FIGURE_MAX_HEIGHT_ATTR = 'data-rt-max-height';
export const RTE_FIGURE_DIMENSIONS_ATTR = 'data-rt-dimensions';
export const RTE_FIGURE_PAGE_URL_ATTR = 'data-page-url';
export const RTE_FIGURE_DATA_ATTRS: Array<string> = [
  RTE_FIGURE_TYPE_ATTR,
  RTE_FIGURE_ALIGN_ATTR,
  RTE_FIGURE_MAX_WIDTH_ATTR,
  RTE_FIGURE_MAX_HEIGHT_ATTR,
  RTE_FIGURE_DIMENSIONS_ATTR,
  RTE_FIGURE_PAGE_URL_ATTR,
];

/** RTE embed attributes, used to support embeds via supported CSV exported action within RTE */
export const RTE_EMBED_TYPE_ATTR = 'data-rt-embed-type';

export const HREF = 'href';
export const HREF_DISABLED = HREF + '-disabled';
export const HREF_DISABLED_DEFAULT_COLOR = HREF_DISABLED + '-default-color';
export const HREF_DISABLED_UNDERLINE = HREF_DISABLED + '-underline';
export const MAILTO_EMAIL_REGEX = /^mailto:([^?$]*)/;
export const MAILTO_SUBJECT_REGEX = /^mailto:.*\?.*subject=([^&$]+)/;
export const TEL_NUMBER_REGEX = /^tel:([^$]*)/;
export const DEPRECATED_WF_GOOGLE_MAPS_KEY =
  'AIzaSyBQ4EYEg4aRz9-yiCnerTV7bk8GCWgZOhk';

export const RTE_FIGURE_TYPE_CLASS = 'w-richtext-figure-type-';
export const RTE_FIGURE_TYPE_IMAGE_CLASS = RTE_FIGURE_TYPE_CLASS + 'image';
export const RTE_FIGURE_TYPE_VIDEO_CLASS = RTE_FIGURE_TYPE_CLASS + 'video';

export const FIGURE_ALIGN_MAP: {
  center: 'w-richtext-align-center';
  fullwidth: 'w-richtext-align-fullwidth';
  normal: 'w-richtext-align-normal';
  floatleft: 'w-richtext-align-floatleft';
  floatright: 'w-richtext-align-floatright';
} = {
  center: 'w-richtext-align-center',
  fullwidth: 'w-richtext-align-fullwidth',
  normal: 'w-richtext-align-normal',
  floatleft: 'w-richtext-align-floatleft',
  floatright: 'w-richtext-align-floatright',
};

/** An attribute used to mark newly created typographic content node links and span nodes so that we can auto-select them after the RTE unbinds in the designer-canvas. */
export const RTE_LEGACY_TEMP_ELEM_ATTR = 'wf-temp-elem';
export const RTE_NEW_ELEMENT_MARKER_ATTR = 'wf-rte-new-element';

export const TYPOGRAPHIC_TAG_REGEX =
  /^(?:h[1-6]|p|blockquote|span|label|button|q|i|em|strong|b|u|s|small|abbr|cite|dfn|sub|sup|time|code|kbd)$/;

export const richTextPageAnchorRegex = new RegExp(
  '<a[^>]+' + RTE_LINK_PAGE_ID_ATTR + '[^>]+>',
  'g'
);
/** used to replace the href value from an opening anchor tag matched in richTextPageAnchorRegex. $0 is the attr="val", $1 is just val */
export const richTextHrefReplaceRegex = /href=(?:"|')([^'"]+)(?:"|')/;
/** used on the opening tag of an anchor to remove the RTE link attributes. (mostly on publish and export) */
export const richTextLinkAttrsReplaceRegex = new RegExp(
  '(' +
    [
      RTE_LINK_TYPE_ATTR,
      RTE_LINK_PAGE_ID_ATTR,
      RTE_LINK_ITEM_ID_ATTR,
      RTE_LINK_COLLECTION_ID_ATTR,
      RTE_LINK_DOM_ID_ATTR,
    ].join('|') +
    ')=(\'|")[^\'"]*(\'|") ?',
  'g'
);
/** used to match the value of the static page id attribute (RTE_LINK_PAGE_ID_ATTR) */
export const richTextPageAttrRegex = new RegExp(
  RTE_LINK_PAGE_ID_ATTR + '=(?:"|\')([^\'"]+)(?:"|\')'
);

/** Date sites have to be published after to have the editor code published on them. */
const DYNAMO_PUBLISH_DATE = new Date('2015/10/07 07:00:00 GMT+0000'); // Update on public release.
export const editorPublishDate: Date = DYNAMO_PUBLISH_DATE;

/** used for detecting legacy micro node ids */
export const microNodeIdLength = 8;

export const DYN_BOUND_PLACEHOLDER_TEXT = '[dynamic-text]';

export const allowedTypoContentTags: Array<string> = [
  'a',
  'span',
  'strong',
  'em',
  'br',
];

/** Nodes that should not be closed in HTML5 */
export const VOID_HTML_TAGS: Set<string> = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'keygen',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

type RichTextSanitizerOptions = {
  tags: {
    [key: string]:
      | boolean
      | {
          [key: string]: boolean;
        };
  };
};

export const richTextSanitizerOptions: RichTextSanitizerOptions = {
  tags: {
    p: true,
    div: {class: true},
    span: true,
    a: {href: true},
    strong: true,
    ul: true,
    ol: true,
    li: true,
    br: true,
    b: true,
    i: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
    img: {
      src: true,
      style: true,
    },
    blockquote: true,
  },
};
